import React, { useState, forwardRef, useImperativeHandle, useMemo, useEffect, useRef } from 'react';
import { Drawer, Button, Popover, Steps, Form, Input, Radio, notification, Space, Select, Alert, Popconfirm, Switch } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import TradingPartnersAPI from '../../../restAPI/TradingPartnersAPI';
import CompanyAPI from '../../../restAPI/CompanyAPI';
import validationRules from '../../../assets/validation/rules';
import validateForm from '../../../utils/FormValidation';

const popoverContentTIN = (
  <div>
    If TIN No is not available, please
    use: <br />
    EI00000000010: General Public <br />
    EI00000000020: Foreign Buyer<br />
    EI00000000030: Foreign Supplier<br />
    EI00000000040: Government Entity
  </div>
);

const popoverContentReg = (
  <div>
    For individuals,
    select and input the NRIC, <br />Passport
    number or Army number.
  </div>
);
const popoverContentMSIC = (
  <div>
    Refer to link below to
    obtain the correct code:<br />
    <a href="https://sdk.myinvois.hasil.gov.my/codes
/msic-codes/" target="_blank">
      https://sdk.myinvois.hasil.gov.my/codes
      /msic-codes</a><br />
    You can use "00000" if Not Applicable.
  </div>
);

const Context = React.createContext({
  name: 'Default',
});
const { Step } = Steps;
const { TextArea } = Input;
const onSearch = (value) => {
  console.log('search:', value);
};
const StepForm = forwardRef(({ onRefresh }, ref) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [visible, setVisible] = useState(false); // Track drawer visibility
  const { token } = useSelector(state => state.auth);
  const [countryCodes, setCountryCodes] = useState([]);
  const [iSGetCountryCodes, setISGetCountryCodes] = useState(true);
  const [stateCodes, setStateCodes] = useState([]);
  const [iSGetStateCodes, setISGetStateCodes] = useState(true);
  const [msicCodes, setMsicCodes] = useState([]);
  const [iSGetMsicCodes, setISGetMsicCodes] = useState(true);
  const [classification, setclassification] = useState([]);
  const [registrationNumberFieldDisable, setRegistrationNumberFieldDisable] = useState(false);
  const [registrationNumberDBValue, setRegistrationNumberDBValue] = useState("");
  const [registrationTypeDBValue, setRegistrationTypeDBValue] = useState("");
  const [payload, setPayload] = useState({
    "_id": "",
    "tradingPartnerName": "",
    "tin": "",
    "registrationType": "",
    "registrationValue": "",
    "sst": "",
    "ttx": "",
    "msicCode": "",
    "msicDescription": "",
    "countryCode": "",
    "countryName": "",
    "status": 1,
    "email": "",
    "contactNumber": "",
    "addressLine1": "",
    "addressLine2": "",
    "addressLine3": "",
    "postalZone": "",
    "city": "",
    "stateName": "",
    "stateCode": "",
    "groupId": "",
    "branch": false
  });
  const [errors, setErrors] = useState({});
  const [isModified, setIsModified] = useState(false);
  const [showPopconfirm, setShowPopconfirm] = useState(false);
  const [cancelButtonDisabled, setCancelButtonDisabled] = useState(false);
  const [actionState, setActionState] = useState('update');
  const [isTPCompanyCodeRequired, setIsTPCompanyCodeRequired] = useState(false);
  const notificationShown = useRef(false);

  // Expose methods to the parent component
  useImperativeHandle(ref, () => ({
    openDrawer: () => {
      setPayload({ _id: ref.id });
      getTradingPartnerById(ref.id);
      setVisible(true); // Open drawer
    },
    closeDrawer: () => {
      setVisible(false); // Close drawer
      if (onRefresh) onRefresh(); // Trigger refresh on close
    },
  }));

  const [api, contextHolder] = notification.useNotification();

  const openNotificationWithIcon = (type, message, description, placement) => {
    if (!notificationShown.current) {
      notification[type]({
        message: message,
        description: description,
        placement,
      });
      notificationShown.current = true;
      setTimeout(() => {
        notificationShown.current = false;
      }, 5000);
    }
  };

  const contextValue = useMemo(
    () => ({
      name: 'Ant Design',
    }),
    [],
  );

  const checkMandatoryValidations = () => {
    const validateData = {
      tradingPartnerName: payload.tradingPartnerName,
      country: payload.countryName,
      country: payload.countryCode,
      tin: payload.tin,
      sst: payload.sst,
      ttx: payload.ttx,
      registrationType: payload.registrationType,
      registrationValue: payload.registrationValue,
      msicCode: payload.msicCode,
      businessActivityDescription: payload.msicDescription,
    }
    
    // Add Company Code validation if required
    if (isTPCompanyCodeRequired) {
      validateData.companyCode = payload.companyCode;
    }
    
    const validationResult = validateForm(validateData, validationRules);

    if (!validationResult.isValid) {
      const validationErr = validationResult.errors;
      const newErrors = {};
      Object.keys(validationErr).forEach((value) => {
        newErrors[value] = validationErr[value];
      })
      setErrors(newErrors);
      return false;
    } else {
      return true;
    }
  }

  const next = () => {
    const validationStatus = checkMandatoryValidations();
    if (validationStatus) {
      getLhdnTinNumberValidation();
    }
  }

  const prev = () => setCurrentStep(currentStep - 1);

  // Function to fetch company details and check isTPCompanyCodeRequired
  const getCompanyDetails = async () => {
    try {
      const companyId = localStorage.getItem('companyId');
      if (companyId) {
        const response = await CompanyAPI.getCompanyById(token, localStorage.getItem('groupId'), companyId, companyId);
        if (response?.data) {
          setIsTPCompanyCodeRequired(response.data.isTPCompanyCodeRequired || false);
        }
      }
    } catch (error) {
      console.error('Error fetching company details:', error);
    }
  };

  useEffect(() => {
    GetClassificationCodes();
    getCompanyDetails(); // Fetch company details to check isTPCompanyCodeRequired
    if (iSGetCountryCodes) {
      getCountryCodes();
      setISGetCountryCodes(false);
    }

    if (iSGetStateCodes) {
      getStateCodes();
      setISGetStateCodes(false);
    }

    if (iSGetMsicCodes) {
      getMsicCodes();
      setISGetMsicCodes(false);
    }
  }, []);

  const getTradingPartnerById = async (id) => {
    await TradingPartnersAPI.getTradingPartnerById(token, localStorage.getItem('groupId'), localStorage.getItem('companyId'), id).then((res) => {
      if (res?.data) {
        let status = 1;
        if (res.data.status) {
          status = 1;
        }
        else {
          status = 0;
        }
        if (res.data.registrationType === "FOREIGN ENTITY") {
          setRegistrationNumberFieldDisable(false);
        }

        setRegistrationTypeDBValue(res.data.registrationType);
        setRegistrationNumberDBValue(res.data.registrationValue);

        setPayload({
          "_id": res.data._id,
          "tradingPartnerName": res.data.tradingPartnerName,
          "tin": res.data.tin,
          "registrationType": res.data.registrationType,
          "registrationValue": res.data.registrationValue,
          "sst": res.data.sst,
          "ttx": res.data.ttx,
          "msicCode": res.data.msicCode.code,
          "msicDescription": res.data.msicCode.description,
          "countryCode": res.data.country.code,
          "countryName": res.data.country.name,
          "status": status,
          "email": res.data.emailAddress,
          "contactNumber": res.data.contactNumber,
          "addressLine1": res.data.address.addressLine1,
          "addressLine2": res.data.address.addressLine2,
          "addressLine3": res.data.address.addressLine3,
          "postalZone": res.data.address.postalZone,
          "city": res.data.address.city,
          "stateName": (res.data.country.code !== "MYS") ? "Not Applicable" : res.data.address.state.name,
          "stateCode": (res.data.country.code !== "MYS") ? "17" : res.data.address.state.code,
          "groupId": res.data.groupId,
          "classificationCode": res.data.classificationCode === null ? "N/A" : res.data.classificationCode,
          "companyCode": res.data.companyCode,
          "branch": res.data.isBranch || false
        });

        if (res.data.country.code !== "MYS") {
          setStateCodes([]);
        }
      }
    }).catch(err => {
      console.log("error getting trading partner data :", err.message);
    })
  };

  const getCountryCodes = async () => {
    await TradingPartnersAPI.getCountryCodes(token, localStorage.getItem('groupId'), localStorage.getItem('companyId')).then((res) => {
      if (res?.data) {
        const rows = [];
        res.data.map((rowValue) => {
          rows.push({ value: rowValue.code, label: rowValue.name.toLowerCase().split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') })
        });
        rows.sort((a, b) => a.label.localeCompare(b.label));
        const malaysiaIndex = rows.findIndex(item => item.value.toUpperCase() === "MYS");
        const [malaysia] = rows.splice(malaysiaIndex, 1);
        rows.unshift(malaysia);
        setCountryCodes(rows);

      }
    }).catch(err => {
      console.log("error getting contry codes data :", err.message);
    })
  };
  const GetClassificationCodes = async () => {
    await TradingPartnersAPI.GetClassificationCodes(token, localStorage.getItem('groupId'), /*companyId*/ localStorage.getItem('companyId'))
      .then((res) => {
        if (res?.data) {
          const classification = res.data.map((rowValue) => ({
            value: rowValue.code,
            label: rowValue.code
          })).sort((a, b) => a.label.localeCompare(b.label));
          const NAclassification = {
            value: "N/A",
            label: "N/A"
          };
          // Add "N/A" option at the beginning of the classification array
          classification.unshift(NAclassification);
          setclassification(classification);
        }
      })
      .catch((err) => {
        if (err.response) {
          openNotificationWithIcon('error', 'No Document Type found', `${err.response.data.message}`, 'bottomLeft');
        } else {
          openNotificationWithIcon('error', 'No Document Type found', `${err.message}`, 'bottomLeft');
        }
      });
  }

  const getStateCodes = async () => {
    await TradingPartnersAPI.getStateCodes(token, localStorage.getItem('groupId'), localStorage.getItem('companyId')).then((res) => {
      if (res?.data) {
        const rows = [];
        res.data.map((rowValue) => {
          rows.push({ value: rowValue.code, label: rowValue.state })
        });
        setStateCodes(rows);
      }
    }).catch(err => {
      console.log("error getting state codes data :", err.message);
    })
  };

  const getMsicCodes = async () => {
    await TradingPartnersAPI.getMsciCodes(token, localStorage.getItem('groupId'), localStorage.getItem('companyId')).then((res) => {
      if (res?.data) {
        const rows = [];
        res.data.sort((a, b) => a.code.localeCompare(b.code));
        res.data.map((rowValue) => {
          rows.push({
            value: rowValue.code,
            label: `${rowValue.code} - ${rowValue.description}`, //string for search
            code: rowValue.code,
            description: rowValue.description,
          });
        });
        setMsicCodes(rows);
      }
    }).catch(err => {
      console.log("error getting msic codes data :", err.message);
    })
  };

  const getLhdnTinNumberValidation = async () => {
    await TradingPartnersAPI.getLhdnTinNumberValidation(token, localStorage.getItem('groupId'), localStorage.getItem('companyId'), payload.tin, payload.registrationType, payload.registrationValue).then((res) => {
      if (res) {
        setCurrentStep(currentStep + 1);
      }
    }).catch(err => {
      const newErrors = { "tin": "TIN is invalid" };
      setErrors(prev => ({ ...prev, ...newErrors }));
    })
  }

  const OnStateChange = (value) => {
    let state = stateCodes.filter(function (obj) {
      return obj.value === value
    });
    if (state.length > 0) {
      state.map((rowValue) => {
        setPayload({
          "_id": payload._id,
          "tradingPartnerName": payload.tradingPartnerName,
          "tin": payload.tin,
          "registrationType": payload.registrationType,
          "registrationValue": payload.registrationValue,
          "sst": payload.sst,
          "ttx": payload.ttx,
          "msicCode": payload.msicCode,
          "msicDescription": payload.msicDescription,
          "countryCode": payload.countryCode,
          "countryName": payload.countryName,
          "status": payload.status,
          "email": payload.email,
          "contactNumber": payload.contactNumber,
          "addressLine1": payload.addressLine1,
          "addressLine2": payload.addressLine2,
          "addressLine3": payload.addressLine3,
          "postalZone": payload.postalZone,
          "city": payload.city,
          "stateName": rowValue.label,
          "stateCode": rowValue.value,
          "groupId": localStorage.getItem('groupId'),
          "classificationCode": payload.classificationCode === null ? "N/A" : payload.classificationCode,
          "companyCode": payload.companyCode
        });
      });
      setErrors({});
      setIsModified(true);
    }
  }

  const OnCountryChange = (value) => {
    let contry = countryCodes.filter(function (obj) {
      return obj.value === value
    });

    if (contry.length > 0) {
      contry.map((rowValue) => {
        setPayload({
          "_id": payload._id,
          "tradingPartnerName": payload.tradingPartnerName,
          "tin": payload.tin,
          "registrationType": payload.registrationType,
          "registrationValue": payload.registrationValue,
          "sst": payload.sst,
          "ttx": payload.ttx,
          "msicCode": payload.msicCode,
          "msicDescription": payload.msicDescription,
          "countryCode": rowValue.value,
          "countryName": rowValue.label,
          "status": payload.status,
          "email": payload.email,
          "contactNumber": payload.contactNumber,
          "addressLine1": payload.addressLine1,
          "addressLine2": payload.addressLine2,
          "addressLine3": payload.addressLine3,
          "postalZone": payload.postalZone,
          "city": payload.city,
          "stateName": payload.stateName,
          "stateCode": payload.stateCode,
          "groupId": localStorage.getItem('groupId'),
          "classificationCode": payload.classificationCode === null ? "N/A" : payload.classificationCode,
          "companyCode": payload.companyCode
        });
      });
      setErrors({});
      setIsModified(true);
    }

    if (value === "MYS") {
      getStateCodes();
      setISGetStateCodes(false);
      setPayload((prev) => ({
        ...prev,
        stateName: "",
        stateCode: ""
      }));
    }
    else {
      setStateCodes([]);
      setPayload((prev) => ({
        ...prev,
        stateName: "Not Applicable",
        stateCode: "17"
      }));
    }
  }

  const OnRegistrationTypeChange = async (value) => {
    let registrationNumberValue = "";
    const genaratedId = await generateCustomID();
    const requiresGeneratedID = (type) => ["FOREIGN ENTITY", "GOV ENTITY"].includes(type);

    if (registrationTypeDBValue === value) {
      registrationNumberValue = registrationNumberDBValue;
    } else if (requiresGeneratedID(value)) {
      registrationNumberValue = genaratedId;
    }

    setRegistrationNumberFieldDisable(false);

    setPayload({
      "_id": payload._id,
      "tradingPartnerName": payload.tradingPartnerName,
      "tin": payload.tin,
      "registrationType": value,
      "registrationValue": registrationNumberValue,
      "sst": payload.sst,
      "ttx": payload.ttx,
      "msicCode": payload.msicCode,
      "msicDescription": payload.msicDescription,
      "countryCode": payload.countryCode,
      "countryName": payload.countryName,
      "status": payload.status,
      "email": payload.email,
      "contactNumber": payload.contactNumber,
      "addressLine1": payload.addressLine1,
      "addressLine2": payload.addressLine2,
      "addressLine3": payload.addressLine3,
      "postalZone": payload.postalZone,
      "city": payload.city,
      "stateName": payload.stateName,
      "stateCode": payload.stateCode,
      "groupId": localStorage.getItem('groupId'),
      "classificationCode": payload.classificationCode ?? "N/A",
      "companyCode": payload.companyCode
    });
    setErrors({});
    setIsModified(true);
  }

  function validateFormData(data) {
    const validationResult = validateForm(data, validationRules)
    if (!validationResult.isValid) {
      const err = validationResult.errors
      const newErrors = {};
      Object.keys(err).forEach((value) => {
        newErrors[value] = err[value];
      })
      setErrors(prev => ({ ...prev, ...newErrors }));
    } else {
      const keyData = Object.keys(data)[0]
      setErrors(prev => ({ ...prev, [keyData]: '' }))
    }
  }

  const onRadioChange = (e) => {
    setPayload({
      "_id": payload._id,
      "tradingPartnerName": payload.tradingPartnerName,
      "tin": payload.tin,
      "registrationType": payload.registrationType,
      "registrationValue": payload.registrationValue,
      "sst": payload.sst,
      "ttx": payload.ttx,
      "msicCode": payload.msicCode,
      "msicDescription": payload.msicDescription,
      "countryCode": payload.countryCode,
      "countryName": payload.countryName,
      "status": e.target.value,
      "email": payload.email,
      "contactNumber": payload.contactNumber,
      "addressLine1": payload.addressLine1,
      "addressLine2": payload.addressLine2,
      "addressLine3": payload.addressLine3,
      "postalZone": payload.postalZone,
      "city": payload.city,
      "stateName": payload.stateName,
      "stateCode": payload.stateCode,
      "groupId": localStorage.getItem('groupId'),
      "classificationCode": payload.classificationCode === null ? "N/A" : payload.classificationCode,
      "companyCode": payload.companyCode
    });
    setIsModified(true);
  };

  const OnMsicChange = (value) => {
    let msic = msicCodes.filter(function (obj) {
      return obj.value === value
    });

    if (msic.length > 0) {
      msic.map((rowValue) => {
        setPayload({
          "_id": payload._id,
          "tradingPartnerName": payload.tradingPartnerName,
          "tin": payload.tin,
          "registrationType": payload.registrationType,
          "registrationValue": payload.registrationValue,
          "sst": payload.sst,
          "ttx": payload.ttx,
          "msicCode": rowValue.value,
          "msicDescription": rowValue.description,
          "countryCode": payload.countryCode,
          "countryName": payload.countryName,
          "status": payload.status,
          "email": payload.email,
          "contactNumber": payload.contactNumber,
          "addressLine1": payload.addressLine1,
          "addressLine2": payload.addressLine2,
          "addressLine3": payload.addressLine3,
          "postalZone": payload.postalZone,
          "city": payload.city,
          "stateName": payload.stateName,
          "stateCode": payload.stateCode,
          "groupId": localStorage.getItem('groupId'),
          "classificationCode": payload.classificationCode === null ? "N/A" : payload.classificationCode,
          "companyCode": payload.companyCode
        });
      });
      setErrors({});
      setIsModified(true);
    }
  }

  const validateBeforeSubmit = () => {
    const validateData = {
      tradingPartnerName: payload.tradingPartnerName,
      country: payload.countryName,
      country: payload.countryCode,
      tin: payload.tin,
      sst: payload.sst,
      ttx: payload.ttx,
      registrationType: payload.registrationType,
      registrationValue: payload.registrationValue,
      msicCode: payload.msicCode,
      businessActivityDescription: payload.msicDescription,
      tpemail: payload.email,
      contactNumber: payload.contactNumber,
      addressLine1: payload.addressLine1,
      addressLine2: payload.addressLine2,
      addressLine3: payload.addressLine3,
      postalZone: payload.postalZone,
      city: payload.city,
      state: payload.stateName,
      state: payload.stateCode,
    }
    
    // Add Company Code validation if required
    if (isTPCompanyCodeRequired) {
      validateData.companyCode = payload.companyCode;
    }
    
    const validationResult = validateForm(validateData, validationRules);

    if (!validationResult.isValid) {
      const validationErr = validationResult.errors;
      const newErrors = {};
      Object.keys(validationErr).forEach((value) => {
        newErrors[value] = validationErr[value];
      })
      setErrors(newErrors);
      return false;
    } else {
      return true;
    }
  }

  const onSubmit = async () => {
    setShowPopconfirm(false);
    const formData = {
      "tradingPartnerName": payload.tradingPartnerName,
      "tin": payload.tin,
      "registrationType": payload.registrationType,
      "registrationValue": payload.registrationValue,
      "sst": payload.sst,
      "ttx": payload.ttx,
      "msicCode": {
        "code": payload.msicCode,
        "description": payload.msicDescription,
      },
      "country": {
        "code": payload.countryCode,
        "name": payload.countryName,
      },
      "status": payload.status,
      "emailAddress": payload.email,
      "contactNumber": payload.contactNumber,
      "address": {
        "addressLine1": payload.addressLine1,
        "addressLine2": payload.addressLine2,
        "addressLine3": payload.addressLine3,
        "postalZone": payload.postalZone,
        "city": payload.city,
        "state": {
          "name": payload.stateName,
          "code": payload.stateCode,
        }
      },
      "groupId": localStorage.getItem('groupId'),
      "classificationCode": payload.classificationCode,
      "isBranch": payload.branch
    };
    
    // Only include companyCode if it has a value
    if (payload.companyCode && payload.companyCode.trim() !== '') {
      formData.companyCode = payload.companyCode;
    }

    await TradingPartnersAPI.updateTradingPartner(token, localStorage.getItem('groupId'), localStorage.getItem('companyId'), payload._id, formData)
      .then((response) => {
        onClose();
        openNotificationWithIcon('success', "Successful", `${response.message}`, 'bottomLeft');
      })
      .catch((err) => {
        if (err.response) {
          openNotificationWithIcon('error', 'Trading partner update failed', `${err.response.data.message}`, 'bottomLeft');
        } else {
          openNotificationWithIcon('error', 'Trading partner update failed', `${err.message}`, 'bottomLeft');
        }
      });
  }
  // Handle input changes
  const handleInputChange = (field, value) => {
    setPayload((prev) => ({ ...prev, [field]: value }));
    setIsModified(true)
  };
  const handleSelectChange = (field) => (value) => {
    setPayload((prev) => ({ ...prev, [field]: value }));
    setIsModified(true)
  };
  const onClose = () => {
    setVisible(false);
    if (onRefresh) onRefresh(); // Trigger refresh on drawer close
    setPayload({
      "_id": "",
      "tradingPartnerName": "",
      "tin": "",
      "registrationType": "",
      "registrationValue": "",
      "sst": "",
      "ttx": "",
      "msicCode": "",
      "msicDescription": "",
      "countryCode": "",
      "countryName": "",
      "status": 1,
      "email": "",
      "contactNumber": "",
      "addressLine1": "",
      "addressLine2": "",
      "addressLine3": "",
      "postalZone": "",
      "city": "",
      "stateName": "",
      "stateCode": "",
      "groupId": "",
      "classificationCode": "",
      "companyCode": ""
    });
    setErrors({});
    setCurrentStep(0);
    setIsModified(false);
  }

  const onClearChange = () => {
    if (onRefresh) onRefresh(); // Trigger refresh on drawer close
    getTradingPartnerByIdForClear(payload._id);
    setErrors({});
  }

  const getTradingPartnerByIdForClear = async (id) => {
    setIsModified(false);
    await TradingPartnersAPI.getTradingPartnerById(token, localStorage.getItem('groupId'), localStorage.getItem('companyId'), id).then((res) => {
      if (res?.data) {
        let status = 1;
        if (res.data.status) {
          status = 1;
        }
        else {
          status = 0;
        }

        if (currentStep == 0) {

          if (res.data.email != payload.email) {
            setIsModified(true);
          } if (res.data.contactNumber != payload.contactNumber) {
            setIsModified(true);
          } if (res.data.address.addressLine1 != payload.addressLine1) {
            setIsModified(true);
          } if (res.data.address.addressLine2 != payload.addressLine2) {
            setIsModified(true);
          } if (res.data.address.addressLine3 != payload.addressLine3) {
            setIsModified(true);
          } if (res.data.address.postalZone != payload.postalZone) {
            setIsModified(true);
          } if (res.data.address.city != payload.city) {
            setIsModified(true);
          } if (res.data.address.state.code != payload.stateCode) {
            setIsModified(true);
          }
          if (res.data.classificationCode != payload.classificationCode) {
            setIsModified(true);
          }
          if (res.data.companyCode != payload.companyCode) {
            setIsModified(true);
          }
          setPayload({
            "_id": res.data._id,
            "tradingPartnerName": res.data.tradingPartnerName,
            "tin": res.data.tin,
            "registrationType": res.data.registrationType,
            "registrationValue": res.data.registrationValue,
            "sst": res.data.sst,
            "ttx": res.data.ttx,
            "msicCode": res.data.msicCode.code,
            "msicDescription": res.data.msicCode.description,
            "countryCode": res.data.country.code,
            "countryName": res.data.country.name,
            "status": status,
            "email": payload.email,
            "contactNumber": payload.contactNumber,
            "addressLine1": payload.addressLine1,
            "addressLine2": payload.addressLine2,
            "addressLine3": payload.addressLine3,
            "postalZone": payload.postalZone,
            "city": payload.city,
            "stateName": (res.data.country.code !== "MYS") ? "Not Applicable" : res.data.address.state.name,
            "stateCode": (res.data.country.code !== "MYS") ? "17" : res.data.address.state.code,
            "groupId": payload.groupId,
            "classificationCode": res.data.classificationCode === null ? "N/A" : res.data.classificationCode,
            "companyCode": res.data.ompanyCode
          });
        } else if (currentStep == 1) {

          if (res.data.tradingPartnerName != payload.tradingPartnerName) {
            setIsModified(true);
          } if (res.data.tin != payload.tin) {
            setIsModified(true);
          } if (res.data.registrationType != payload.registrationType) {
            setIsModified(true);
          }
          if (res.data.registrationValue != payload.registrationValue) {
            setIsModified(true);
          }
          if (res.data.sst != payload.sst) {
            setIsModified(true);
          }
          if (res.data.ttx != payload.ttx) {
            setIsModified(true);
          }
          if (res.data.msicCode.code != payload.msicCode) {
            setIsModified(true);
          }
          if (res.data.msicCode.description != payload.msicDescription) {
            setIsModified(true);
          }
          if (res.data.country.countryCode != payload.countryCode) {
            setIsModified(true);
          }

          setPayload({
            "_id": res.data._id,
            "tradingPartnerName": payload.tradingPartnerName,
            "tin": payload.tin,
            "registrationType": payload.registrationType,
            "registrationValue": payload.registrationValue,
            "sst": payload.sst,
            "ttx": payload.ttx,
            "msicCode": payload.msicCode,
            "msicDescription": payload.description,
            "countryCode": payload.countryCode,
            "countryName": payload.countryName,
            "status": payload.status,
            "email": res.data.emailAddress,
            "contactNumber": res.data.contactNumber,
            "addressLine1": res.data.address.addressLine1,
            "addressLine2": res.data.address.addressLine2,
            "addressLine3": res.data.address.addressLine3,
            "postalZone": res.data.address.postalZone,
            "city": res.data.address.city,
            "stateName": (res.data.country.code !== "MYS") ? "Not Applicable" : res.data.address.state.name,
            "stateCode": (res.data.country.code !== "MYS") ? "17" : res.data.address.state.code,
            "groupId": res.data.groupId,
            "classificationCode": payload.classificationCode === null ? "N/A" : payload.classificationCode,
            "companyCode": payload.companyCode
          });
        }
        //---------------------------------------added to disable state cpde text box ----------------------
        if (res.data.country.code !== 'MYS') {
          setStateCodes([]);
        }
      }
    }).catch(err => {
      console.log("error getting trading partner data :", err.message);
    })
  };

  const handleCreateUpdateClick = (e) => {
    e.preventDefault();
    let validation = validateBeforeSubmit();
    if (validation) {
      setShowPopconfirm(true)
    } else {
      setShowPopconfirm(false)
    }
  }

  const handleCancel = () => {
    setCancelButtonDisabled(true);
    setTimeout(() => {
      setShowPopconfirm(false);
      setCancelButtonDisabled(false);
    }, 300);
  };

  const generateCustomID = async () => {
    const date = new Date();

    // Format date to YYMMDD
    const yy = String(date.getFullYear()).slice(2);
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");

    const datePart = `${yy}${mm}${dd}`;

    // Generate 6 random digits
    const randomPart = Math.floor(100000 + Math.random() * 9000); // ensures 6 digits

    return `${datePart}${randomPart}`;
  };

  // Content for each step
  const renderStepContent = () => {
    if (currentStep === 0) {
      return (
        <Form layout="vertical">
          {/* <Alert message="* Fields are required" type="warning" /> */}

          <Form.Item label="Trading Partner Name" validateStatus={errors.tradingPartnerName ? 'error' : ''} help={errors.tradingPartnerName || ''} required>
            <Input size="large" name="tradingPartnerName" value={payload["tradingPartnerName"]} onChange={(e) => { setPayload(prev => ({ ...prev, tradingPartnerName: e.target.value })); const validateData = { tradingPartnerName: e.target.value }; validateFormData(validateData); setIsModified(true); }} placeholder="Trading Partner Name" />
          </Form.Item>

          <Form.Item label="Country" validateStatus={errors.country ? 'error' : ''} help={errors.country || ''} required>
            <Select
              showSearch
              name='country'
              placeholder="Select country"
              value={payload["countryCode"] || "Select a Country"}
              optionFilterProp="label"
              onChange={OnCountryChange}
              size='large'
              options={countryCodes}
            />
          </Form.Item>
          <Form.Item 
            label="Company Code" 
            validateStatus={errors.companyCode ? 'error' : ''} 
            help={errors.companyCode || ''}
            required={isTPCompanyCodeRequired}
          >
            <Input 
              size="large" 
              name="companyCode" 
              placeholder="Company Code" 
              onChange={(e) => handleInputChange("companyCode", e.target.value)} 
              value={payload["companyCode"]} 
              required={isTPCompanyCodeRequired}
            />
          </Form.Item>
          <Form.Item label="Classification Code" >
            <Select
              showSearch
              name="classificationCode"
              placeholder="Select Classification Code"
              optionFilterProp="label"
              value={payload["classificationCode"] || "Select a Classification Code"}
              onSelect={handleSelectChange("classificationCode")}
              size='large'
              options={classification}
            />
          </Form.Item>

          <Form.Item label={
            <div style={{ display: 'flex', alignItems: 'center' }}>
              TIN
              <Popover content={popoverContentTIN} title="TIN" trigger="hover">
                <InfoCircleOutlined className="info-icon" />
              </Popover>
            </div>
          } validateStatus={errors.tin ? 'error' : ''} help={errors.tin || ''} required>
            <Input size="large" name="tin" value={payload["tin"]} onChange={(e) => { setPayload(prev => ({ ...prev, tin: e.target.value })); const validateData = { tin: e.target.value }; validateFormData(validateData); setIsModified(true); }} placeholder="TIN" />
          </Form.Item>

          <Form.Item label="SST" validateStatus={errors.sst ? 'error' : ''} help={errors.sst || ''} required>
            <Input size="large" name="sst" value={payload["sst"]} onChange={(e) => { setPayload(prev => ({ ...prev, sst: e.target.value })); const validateData = { sst: e.target.value }; validateFormData(validateData); setIsModified(true); }} placeholder="SST" />
          </Form.Item>

          <Form.Item label="TTX" validateStatus={errors.ttx ? 'error' : ''} help={errors.ttx || ''} required>
            <Input size="large" name="ttx" value={payload["ttx"]} onChange={(e) => { setPayload(prev => ({ ...prev, ttx: e.target.value })); const validateData = { ttx: e.target.value }; validateFormData(validateData); setIsModified(true); }} placeholder="TTX" />
          </Form.Item>

          <Form.Item label={
            <div style={{ display: 'flex', alignItems: 'center' }}>
              Registration Type
              <Popover content={popoverContentReg} title="Registration Type" trigger="hover">
                <InfoCircleOutlined className="info-icon" />
              </Popover>
            </div>
          } validateStatus={errors.registrationType ? 'error' : ''} help={errors.registrationType || ''} required>
            <Select
              showSearch
              name="registrationType"
              placeholder="Select Registration Type"
              optionFilterProp="label"
              value={payload["registrationType"] || "Select a Registration Type"}
              onChange={OnRegistrationTypeChange}
              size='large'
              onSearch={onSearch}
              options={[
                {
                  value: 'NRIC',
                  label: 'NRIC',
                },
                {
                  value: 'Passport number',
                  label: 'Passport number',
                },
                {
                  value: 'Business registration number',
                  label: 'Business registration number',
                },
                {
                  value: 'Army number',
                  label: 'Army number',
                },
                {
                  value: 'FOREIGN ENTITY',
                  label: 'Foreign entity',
                },
                {
                  value: 'GOV ENTITY',
                  label: 'Gov entity',
                }
              ]} />
          </Form.Item>

          <Form.Item
            label="Registration Value"
            validateStatus={errors.registrationValue ? "error" : ""}
            help={errors.registrationValue || ""}
            required
          >
            <Input
              size="large"
              name="registrationValue"
              value={payload["registrationValue"]}
              disabled={payload.branch}
              onChange={(e) => {
                setPayload((prev) => ({
                  ...prev,
                  registrationValue: e.target.value,
                }));
                const validateData = { registrationValue: e.target.value };
                validateFormData(validateData);
              }}
              placeholder="Registration Value"
            />
          </Form.Item>

          <Form.Item label={
            <div style={{ display: 'flex', alignItems: 'center' }}>
              MSIC Code
              <Popover content={popoverContentMSIC} title="MSIC Code" trigger="hover">
                <InfoCircleOutlined className="info-icon" />
              </Popover>
            </div>
          } validateStatus={errors.msicCode ? 'error' : ''} help={errors.msicCode || ''} required>
            <Select
              showSearch
              name="msicCode"
              placeholder="Select MSIC Code"
              optionFilterProp="label"
              value={payload["msicCode"] || "Select a MSIC Code"}
              onChange={OnMsicChange}
              size='large'
              options={msicCodes}
              optionRender={(option) => (
                <div
                  style={{
                    whiteSpace: "normal",
                    lineHeight: "1.5em",
                    maxWidth: "100%",
                    overflowWrap: "break-word",
                  }}
                >
                  <strong>{option.data.code}</strong> - {option.data.description}
                </div>
              )}
              filterOption={(input, option) =>
                (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
              }
            />
          </Form.Item>

          <Form.Item label="Status" required>
            <Radio.Group name="status" value={payload["status"]} onChange={onRadioChange}>
              <Radio value={1}>Active</Radio>
              <Radio value={0}>Inactive</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            label={
              <div style={{ display: 'flex', alignItems: 'center' }}>
                Branch
                <Popover 
                  content="If this is active this trading partner will be consider as branch under the trading partner." 
                  title="Branch" 
                  trigger="hover"
                >
                  <InfoCircleOutlined className="info-icon" />
                </Popover>
              </div>
            }
          >
            <Switch
              checked={payload.branch}
              onChange={(checked) => {
                setPayload((prev) => ({
                  ...prev,
                  branch: checked,
                }));
                setIsModified(true);
              }}
            />
          </Form.Item>

        </Form>
      );
    } else if (currentStep === 1) {
      return <div> <Form layout="vertical">
        <Form.Item label="Email Address" validateStatus={errors.tpemail ? 'error' : ''} help={errors.tpemail || ''}>
          <Input size="large" name="email" value={payload["email"]} onChange={(e) => { setPayload(prev => ({ ...prev, email: e.target.value })); const validateData = { tpemail: e.target.value }; validateFormData(validateData); setIsModified(true); }} placeholder="Email Address" />
        </Form.Item>

        <Form.Item label="Contact Number" validateStatus={errors.contactNumber ? 'error' : ''} help={errors.contactNumber || ''} required >
          <Input size="large" name="contactNumber" value={payload["contactNumber"]} onChange={(e) => { setPayload(prev => ({ ...prev, contactNumber: e.target.value })); const validateData = { contactNumber: e.target.value }; validateFormData(validateData); setIsModified(true); }} placeholder="Contact Number" />
        </Form.Item>


        <Form.Item label="Address">
          <Form.Item label="Address Line 1" validateStatus={errors.addressLine1 ? 'error' : ''} help={errors.addressLine1 || ''} required>
            <Input size="large" name="addressLine1" value={payload["addressLine1"]} onChange={(e) => { setPayload(prev => ({ ...prev, addressLine1: e.target.value })); setIsModified(true); const validateData = { addressLine1: e.target.value }; validateFormData(validateData); }} placeholder="Address Line 1" />
          </Form.Item>

          <Form.Item label="Address Line 2" validateStatus={errors.addressLine2 ? 'error' : ''} help={errors.addressLine2 || ''}>
            <Input size="large" name="addressLine2" value={payload["addressLine2"]} onChange={(e) => { setPayload(prev => ({ ...prev, addressLine2: e.target.value })); setIsModified(true); const validateData = { addressLine2: e.target.value }; validateFormData(validateData); }} placeholder="Address Line 2" />
          </Form.Item>

          <Form.Item label="Address Line 3" validateStatus={errors.addressLine3 ? 'error' : ''} help={errors.addressLine3 || ''}>
            <Input size="large" name="addressLine3" value={payload["addressLine3"]} onChange={(e) => { setPayload(prev => ({ ...prev, addressLine3: e.target.value })); setIsModified(true); const validateData = { addressLine3: e.target.value }; validateFormData(validateData); }} placeholder="Address Line 3" />
          </Form.Item>

          <Form.Item label="Postal Zone" validateStatus={errors.postalZone ? 'error' : ''} help={errors.postalZone || ''}>
            <Input size="large" name="postalZone" value={payload["postalZone"]} onChange={(e) => { setPayload(prev => ({ ...prev, postalZone: e.target.value })); setIsModified(true); const validateData = { postalZone: e.target.value }; validateFormData(validateData); }} placeholder="Postal Zone" />
          </Form.Item>

          <Form.Item label="City Name" validateStatus={errors.city ? 'error' : ''} help={errors.city || ''} required >
            <Input size="large" name="city" value={payload["city"]} onChange={(e) => { setPayload(prev => ({ ...prev, city: e.target.value })); const validateData = { city: e.target.value }; validateFormData(validateData); setIsModified(true); }} placeholder="City Name" />
          </Form.Item>

          <Form.Item label="State" validateStatus={errors.state ? 'error' : ''} help={errors.state || ''} required>
            <Select
              showSearch
              name="state"
              placeholder="Select State"
              optionFilterProp="label"
              value={payload["stateName"] || "Select State"}
              onChange={OnStateChange}
              size='large'
              options={stateCodes}
              disabled={payload.countryCode !== "MYS"}//{stateCodes.length == 0}
            />
          </Form.Item>

        </Form.Item>
      </Form></div>;
    }
    return null;
  };

  return (
    <Context.Provider value={contextValue}>
      {contextHolder}
      <Drawer
        title="Update Trading Partner"
        width={700}
        visible={visible}
        onClose={() => onClose()}
        footer={
          <div style={{ textAlign: 'right' }}>
            <Button style={{ marginRight: 8 }} size="large" onClick={onClearChange}>
              Reset
            </Button>
            {currentStep > 0 && (
              <Button style={{ marginRight: 8 }} size="large" onClick={prev}>
                Previous
              </Button>
            )}
            {currentStep < 1 ? (
              <Button type="primary" size="large" onClick={next}>
                Next
              </Button>
            ) : (
              <Popconfirm
                title={`Do you want to update the trading partner details?`}
                open={showPopconfirm}
                onConfirm={() => onSubmit()}
                onCancel={handleCancel}
                okText="Yes"
                cancelText="No"
                cancelButtonProps={{ disabled: cancelButtonDisabled }}
              >
                <Button type="primary" size="large" disabled={!isModified} onClick={handleCreateUpdateClick}>
                  Update
                </Button>
              </Popconfirm>
            )}
          </div>
        }
      >
        <Steps current={currentStep}>
          <Step title="General Information" />
          <Step title="Contact Information" />
        </Steps>
        <div style={{ marginTop: 24 }}>{renderStepContent()}</div>

      </Drawer>
    </Context.Provider>
  );
});

export default StepForm;
